import {
  ROTULO_TIPO_TECNICO,
  TIPOS_TECNICO,
  type ItemImportacaoUsuario,
  type LinhaImportacao,
  type PerfilResumo,
  type PreviaImportacao,
  type ResultadoImportacao,
  type SetorResumo,
  type TipoTecnico,
} from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { AlertTriangle, Check, Download, FileSpreadsheet, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';

interface Props {
  tipo: 'usuarios' | 'tecnicos';
  /** Só para usuários: as opções de cada linha. */
  setores?: SetorResumo[];
  perfis?: PerfilResumo[];
  aoFechar: () => void;
  aoImportar: () => void;
}

/**
 * Importação de pessoas por planilha, com prévia obrigatória.
 *
 * O arquivo vai para o servidor, que devolve linha por linha o que aconteceria — e só
 * depois de você conferir é que alguém é criado. Foi assim que apareceu, numa lista de 28
 * técnicos, um e-mail `@maxxettelecom.com` sem o "n": uma conta que nasceria com o login
 * errado e só daria sinal no dia em que a pessoa tentasse entrar.
 *
 * Para usuários da equipe, setor e perfil são escolhidos **linha a linha** (decisão de
 * 28/09): sem os dois a pessoa entraria sem saber onde atende, e um padrão aplicado a
 * todos esconderia justamente quem é exceção.
 */
export function ImportarPlanilha({ tipo, setores = [], perfis = [], aoFechar, aoImportar }: Props) {
  const entrada = useRef<HTMLInputElement>(null);
  const [previa, setPrevia] = useState<PreviaImportacao | null>(null);
  const [escolhas, setEscolhas] = useState<
    Record<number, Partial<ItemImportacaoUsuario> & { tipo?: TipoTecnico }>
  >({});
  const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const aCriar = previa?.linhas.filter((l) => l.situacao === 'criar') ?? [];
  const faltaEscolher =
    tipo === 'usuarios'
      ? aCriar.some((l) => !escolhas[l.linha]?.setorId || !escolhas[l.linha]?.perfilId)
      : aCriar.some((l) => !escolhas[l.linha]?.tipo);

  async function analisar(arquivo: File) {
    setErro(null);
    setResultado(null);
    setOcupado(true);
    try {
      const nova = await gestaoApi.analisarImportacao(tipo, arquivo);
      setPrevia(nova);
      setEscolhas({});
    } catch (e) {
      setPrevia(null);
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui ler a planilha');
    } finally {
      setOcupado(false);
    }
  }

  async function importar() {
    if (!previa) return;
    setErro(null);
    setOcupado(true);
    try {
      const r =
        tipo === 'usuarios'
          ? await gestaoApi.confirmarImportacaoUsuarios({
              importacaoId: previa.importacaoId,
              itens: aCriar.map((l) => ({
                linha: l.linha,
                setorId: escolhas[l.linha]!.setorId!,
                perfilId: escolhas[l.linha]!.perfilId!,
                papel: escolhas[l.linha]?.papel ?? 'atendente',
              })),
            })
          : await gestaoApi.confirmarImportacaoTecnicos({
              importacaoId: previa.importacaoId,
              itens: aCriar.map((l) => ({ linha: l.linha, tipo: escolhas[l.linha]!.tipo! })),
            });
      setResultado(r);
      // A análise vale uma importação só: sem isto o botão continuaria clicável e a
      // segunda tentativa morreria num erro que não explica nada.
      setPrevia(null);
      aoImportar();
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui importar');
    } finally {
      setOcupado(false);
    }
  }

  function definir(linha: number, campo: keyof ItemImportacaoUsuario | 'tipo', valor: string) {
    setEscolhas((a) => ({ ...a, [linha]: { ...a[linha], [campo]: valor } }));
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-4xl rounded-card bg-white p-6 shadow-xl dark:bg-neutral-900">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium">
              Importar {tipo === 'usuarios' ? 'atendentes e supervisores' : 'técnicos'}
            </h2>
            <p className="text-sm text-neutral-500">
              Planilha .xlsx com as colunas <strong>Nome</strong>, <strong>Email</strong> e{' '}
              <strong>Senha</strong>. Nada é criado antes de você conferir a prévia.
            </p>
          </div>
          <button type="button" onClick={aoFechar} aria-label="Fechar" className="text-neutral-500">
            <X size={20} />
          </button>
        </header>

        {!previa && !resultado && (
          <div className="space-y-4">
            <input
              ref={entrada}
              type="file"
              className="hidden"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const arquivo = e.target.files?.[0];
                e.target.value = '';
                if (arquivo) void analisar(arquivo);
              }}
            />
            <button
              type="button"
              disabled={ocupado}
              onClick={() => entrada.current?.click()}
              className="flex w-full flex-col items-center gap-2 rounded-card border-2 border-dashed border-neutral-300 p-10 text-neutral-500 hover:border-neutral-400 disabled:opacity-50 dark:border-neutral-700"
            >
              <FileSpreadsheet size={32} />
              <span className="text-sm font-medium">
                {ocupado ? 'Conferindo a planilha…' : 'Escolher planilha'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => void gestaoApi.baixarModeloImportacao(tipo)}
              className="flex items-center gap-1 text-xs text-neutral-500 underline"
            >
              <Download size={14} /> Baixar planilha modelo
            </button>
          </div>
        )}

        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        {resultado && (
          <div className="space-y-3">
            <Aviso tipo="sucesso">
              {resultado.criados} pessoa(s) criada(s).
              {resultado.pulados > 0 && ` ${resultado.pulados} linha(s) não entraram.`}
            </Aviso>
            {resultado.falhas.length > 0 && (
              <Aviso tipo="erro">
                Falharam ao gravar:{' '}
                {resultado.falhas.map((f) => `linha ${f.linha} (${f.erro})`).join('; ')}
              </Aviso>
            )}
            <p className="text-xs text-neutral-500">
              Quem entrou recebe a senha da planilha e é obrigado a trocá-la no primeiro acesso.
            </p>
            <div className="flex justify-end">
              <Botao onClick={aoFechar}>Fechar</Botao>
            </div>
          </div>
        )}

        {previa && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="text-emerald-700 dark:text-emerald-400">
                {previa.total.criar} para criar
              </span>
              {previa.total.existe > 0 && (
                <span className="text-neutral-500">{previa.total.existe} já cadastrado(s)</span>
              )}
              {previa.total.erro > 0 && (
                <span className="text-red-600">{previa.total.erro} com problema</span>
              )}
            </div>

            <div className="max-h-[50vh] overflow-y-auto rounded-card border border-neutral-200 dark:border-neutral-800">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-neutral-50 text-xs text-neutral-500 dark:bg-neutral-800">
                  <tr>
                    <th className="p-2 font-medium">Linha</th>
                    <th className="p-2 font-medium">Nome</th>
                    <th className="p-2 font-medium">E-mail</th>
                    {tipo === 'usuarios' && <th className="p-2 font-medium">Setor</th>}
                    {tipo === 'usuarios' && <th className="p-2 font-medium">Perfil</th>}
                    {tipo === 'usuarios' && <th className="p-2 font-medium">Papel</th>}
                    {tipo === 'tecnicos' && <th className="p-2 font-medium">Time</th>}
                    <th className="p-2 font-medium">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {previa.linhas.map((l) => (
                    <Linha
                      key={l.linha}
                      linha={l}
                      tipo={tipo}
                      setores={setores}
                      perfis={perfis}
                      escolha={escolhas[l.linha]}
                      aoEscolher={definir}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {faltaEscolher && (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                Escolha setor e perfil em todas as linhas que vão ser criadas.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Botao variante="secundario" onClick={aoFechar}>
                Cancelar
              </Botao>
              <Botao
                carregando={ocupado}
                disabled={aCriar.length === 0 || faltaEscolher}
                onClick={() => void importar()}
              >
                Importar {aCriar.length} pessoa(s)
              </Botao>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Linha({
  linha,
  tipo,
  setores,
  perfis,
  escolha,
  aoEscolher,
}: {
  linha: LinhaImportacao;
  tipo: 'usuarios' | 'tecnicos';
  setores: SetorResumo[];
  perfis: PerfilResumo[];
  escolha?: Partial<ItemImportacaoUsuario> & { tipo?: TipoTecnico };
  aoEscolher: (linha: number, campo: keyof ItemImportacaoUsuario | 'tipo', valor: string) => void;
}) {
  const criar = linha.situacao === 'criar';
  const seletor =
    'w-full rounded-lg border border-neutral-300 bg-white px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-950';

  return (
    <tr className="border-t border-neutral-200 dark:border-neutral-800">
      <td className="p-2 text-xs text-neutral-500">{linha.linha}</td>
      <td className="p-2">{linha.nome || <span className="text-neutral-400">—</span>}</td>
      <td className="p-2 font-mono text-xs">{linha.email || '—'}</td>
      {tipo === 'usuarios' && (
        <>
          <td className="p-2">
            {criar && (
              <select
                aria-label={`Setor de ${linha.nome}`}
                className={seletor}
                value={escolha?.setorId ?? ''}
                onChange={(e) => aoEscolher(linha.linha, 'setorId', e.target.value)}
              >
                <option value="">Escolher…</option>
                {setores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </select>
            )}
          </td>
          <td className="p-2">
            {criar && (
              <select
                aria-label={`Perfil de ${linha.nome}`}
                className={seletor}
                value={escolha?.perfilId ?? ''}
                onChange={(e) => aoEscolher(linha.linha, 'perfilId', e.target.value)}
              >
                <option value="">Escolher…</option>
                {perfis.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            )}
          </td>
          <td className="p-2">
            {criar && (
              <select
                aria-label={`Papel de ${linha.nome} no setor`}
                className={seletor}
                value={escolha?.papel ?? 'atendente'}
                onChange={(e) => aoEscolher(linha.linha, 'papel', e.target.value)}
              >
                <option value="atendente">Atendente</option>
                <option value="supervisor">Supervisor</option>
              </select>
            )}
          </td>
        </>
      )}
      {tipo === 'tecnicos' && (
        <td className="p-2">
          {criar && (
            <select
              aria-label={`Time de ${linha.nome}`}
              className={seletor}
              value={escolha?.tipo ?? ''}
              onChange={(e) => aoEscolher(linha.linha, 'tipo', e.target.value)}
            >
              <option value="">Escolher…</option>
              {TIPOS_TECNICO.map((t) => (
                <option key={t} value={t}>
                  {ROTULO_TIPO_TECNICO[t]}
                </option>
              ))}
            </select>
          )}
        </td>
      )}
      <td className="p-2">
        {linha.situacao === 'criar' && (
          <span className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400">
            <Check size={14} /> Criar
          </span>
        )}
        {/* Aviso não bloqueia: é a linha que merece um segundo olhar antes do clique. */}
        {linha.aviso && (
          <span className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {linha.aviso}
          </span>
        )}
        {linha.situacao === 'existe' && (
          <span className="text-xs text-neutral-500">Já existe — pulada</span>
        )}
        {linha.situacao === 'erro' && (
          <span className="flex items-center gap-1 text-xs text-red-600">
            <AlertTriangle size={14} /> {linha.detalhe}
          </span>
        )}
      </td>
    </tr>
  );
}
